import { z } from 'zod';

export const OnboardingItemInputSchema = z.object({
  label: z.string().min(1).max(200),
  category: z.string().max(40).default('general'),
  sortOrder: z.number().int().default(0),
});
export type OnboardingItemInput = z.infer<typeof OnboardingItemInputSchema>;

export const OnboardingStartSchema = z.object({
  employeeId: z.string().min(1),
  notes: z.string().max(2000).optional().nullable(),
  /** When omitted, the default UAE-flavoured pre-board checklist is seeded. */
  items: z.array(OnboardingItemInputSchema).optional(),
});
export type OnboardingStart = z.infer<typeof OnboardingStartSchema>;

export const OnboardingItemUpdateSchema = z.object({
  done: z.boolean().optional(),
  notes: z.string().max(1000).optional().nullable(),
});
export type OnboardingItemUpdate = z.infer<typeof OnboardingItemUpdateSchema>;

export const OffboardingReasonSchema = z.enum([
  'RESIGNATION',
  'TERMINATION',
  'END_OF_CONTRACT',
  'RETIREMENT',
  'OTHER',
]);

export const OffboardingStartSchema = z.object({
  employeeId: z.string().min(1),
  reason: OffboardingReasonSchema,
  noticeStartDate: z.string().date(),
  lastWorkingDate: z.string().date(),
  resignationLetter: z.string().max(5000).optional().nullable(),
  items: z.array(OnboardingItemInputSchema).optional(),
  assets: z
    .array(
      z.object({
        assetName: z.string().min(1).max(120),
        serialNumber: z.string().max(80).optional().nullable(),
      })
    )
    .optional(),
});
export type OffboardingStart = z.infer<typeof OffboardingStartSchema>;

export const OffboardingFinaliseSchema = z.object({
  exitInterviewNotes: z.string().max(5000).optional().nullable(),
  finalSettlementAmount: z.number().nonnegative().optional().nullable(),
  finalSettlementPaid: z.boolean().default(false),
  /**
   * "Yes, I have seen that this person still owes money, and I am finalising
   * anyway." Finalising with an outstanding loan balance is REFUSED without
   * this — see `OffboardingService.finalise` for why the refusal exists and
   * why the loan itself is deliberately left untouched.
   *
   * Optional, so an existing caller with no outstanding balance is unaffected;
   * it only becomes load-bearing for the employee who owes something.
   */
  acknowledgeOutstandingLoans: z.boolean().optional(),
});
export type OffboardingFinalise = z.infer<typeof OffboardingFinaliseSchema>;
